import React from 'react';
import { Link } from 'react-router-dom';

interface BreadcrumbItem {
    label: string;
    href?: string;
    /** When set, runs instead of following `href` (use with unsaved-change guards). */
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

interface PageHeaderProps {
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    eyebrow?: string;
    title?: string;
    subtitle?: React.ReactNode;
    subtitleClassName?: string;
    signalLine?: string;
    actions?: React.ReactNode;
    className?: string;
    breadcrumbs?: BreadcrumbItem[];
}

const PageHeader: React.FC<PageHeaderProps> = ({
    as: Tag = 'h1',
    eyebrow,
    title,
    subtitle,
    subtitleClassName,
    signalLine,
    actions,
    className,
    breadcrumbs,
}) => {
    return (
        <div className={`page-header${className ? ` ${className}` : ''}`}>
            {breadcrumbs && breadcrumbs.length > 0 && (
                <nav className="header-breadcrumbs" aria-label="Breadcrumb">
                    <ol>
                        {breadcrumbs.map((breadcrumb, index) => (
                            <li key={`${breadcrumb.label}-${index}`}>
                                {breadcrumb.href ? (
                                    <Link
                                        to={breadcrumb.href}
                                        onClick={(event) => {
                                            if (!breadcrumb.onClick) return;
                                            event.preventDefault();
                                            breadcrumb.onClick(event);
                                        }}
                                    >
                                        {breadcrumb.label}
                                    </Link>
                                ) : (
                                    <span aria-current="page">{breadcrumb.label}</span>
                                )}
                            </li>
                        ))}
                    </ol>
                </nav>
            )}
            {(title || eyebrow || subtitle || signalLine) && (
                <div className="header-content">
                    {eyebrow && <span className="header-eyebrow">{eyebrow}</span>}
                    {title && <Tag>{title}</Tag>}
                    {subtitle && (
                        <span className={`header-subtitle${subtitleClassName ? ` ${subtitleClassName}` : ''}`}>
                            {subtitle}
                        </span>
                    )}
                    {signalLine && <span className="header-signal-line">{signalLine}</span>}
                </div>
            )}
            {actions && <div className="header-actions">{actions}</div>}
        </div>
    );
};

export default PageHeader;
